import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { PartyIdentifierDTO } from 'support/types';
import { Popup } from 'components/Display/Popup/Popup';
import { Button } from 'components/Form/Button/Button';

import { orderIdentifiers } from '../helpers/orderIdentifiers';

type Props = {
  open: boolean;
  partnerName: string;
  partnerIdentifiers: Array<PartyIdentifierDTO>;
  onConfirm: () => void;
  onCancel: () => void;
};

export const EphemeralPartnerConfirmationModal: FC<Props> = ({
  open,
  partnerName,
  partnerIdentifiers,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const trimmedName = partnerName.trim();
  const hasName = trimmedName.length > 0;
  const hasIdentifiers = partnerIdentifiers.length > 0;
  const orderedIdentifiers = orderIdentifiers(partnerIdentifiers);
  const showCard = hasName || hasIdentifiers;

  const CancelButton = () => (
    <Button variant="secondary" onClick={onCancel} analyticsId="webedi:ephemeral-partner-confirmation-cancel">
      {t('common:cancel')}
    </Button>
  );

  const PrimaryButton = () => (
    <Button variant="primary" onClick={onConfirm} analyticsId="webedi:ephemeral-partner-confirmation-confirm">
      {t('webedi:ephemeralPartnerConfirmation.primary')}
    </Button>
  );

  return (
    <Popup
      open={open}
      onClose={onCancel}
      title={t('webedi:ephemeralPartnerConfirmation.title')}
      subtitle={t('webedi:ephemeralPartnerConfirmation.description')}
      CancelButton={CancelButton}
      PrimaryButton={PrimaryButton}
    >
      {showCard ? (
        <div
          data-testid="ephemeral-partner-card"
          className="flex flex-col rounded-md border border-gray-200 bg-white p-3"
        >
          {hasName ? (
            <span data-testid="ephemeral-partner-name" className="text-sm leading-5 font-normal text-gray-900">
              {trimmedName}
            </span>
          ) : null}
          {hasIdentifiers ? (
            <span data-testid="ephemeral-partner-identifiers" className="text-xs leading-4 font-normal text-gray-500">
              {orderedIdentifiers.map((identifier, index) => (
                <Fragment key={`${identifier.domain}-${identifier.identifier}`}>
                  {index > 0 ? ' • ' : null}
                  {t(`common:partyIdentifier.shortLabel.${identifier.domain}`)} {identifier.identifier}
                </Fragment>
              ))}
            </span>
          ) : null}
        </div>
      ) : null}
    </Popup>
  );
};

/**
 * i18n keys
 * t('common:partyIdentifier.shortLabel.GS1')
 * t('common:partyIdentifier.shortLabel.vatID')
 * t('common:partyIdentifier.shortLabel.internal')
 * t('common:partyIdentifier.shortLabel.federalTaxID')
 * t('common:partyIdentifier.shortLabel.DUNS')
 * t('common:partyIdentifier.shortLabel.VVVO')
 * t('common:partyIdentifier.shortLabel.veterinary')
 * t('common:partyIdentifier.shortLabel.organicControlPointNumber')
 */
